import { useEffect, useState, type ReactNode } from "react";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";

/**
 * LoadBar is a thin line at the top of the page while a page's first data is
 * on its way, so a click always answers at once. Background refreshes of data
 * already on screen never show it.
 */
export function LoadBar() {
  const n = useIsFetching({ predicate: firstLoad });
  const [state, setState] = useState<"" | "on" | "done">("");
  const busy = n > 0;
  useEffect(() => {
    if (busy) { const t = setTimeout(() => setState("on"), 120); return () => clearTimeout(t); }
    setState((s) => (s === "on" ? "done" : ""));
  }, [busy]);
  useEffect(() => {
    if (state !== "done") return;
    const t = setTimeout(() => setState(""), 380);
    return () => clearTimeout(t);
  }, [state]);
  return <div className={`load-bar ${state}`} aria-hidden />;
}

const firstLoad = (q: { state: { data: unknown; fetchStatus: string } }) => q.state.data === undefined && q.state.fetchStatus === "fetching";

/**
 * PageIn shows a page once its first data is in (or after a short wait,
 * whichever comes first), then fades it in whole, so sections don't pop in
 * one by one and push each other down.
 */
export function PageIn({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const start = performance.now();
    let calm = 0, raf = 0, done = false;
    const check = () => {
      if (done) return;
      calm = qc.isFetching({ predicate: firstLoad }) === 0 ? calm + 1 : 0;
      if (calm >= 2 || performance.now() - start > 450) { done = true; setShown(true); return; }
      raf = requestAnimationFrame(check);
    };
    raf = requestAnimationFrame(check);
    return () => { done = true; cancelAnimationFrame(raf); };
  }, [qc]);
  return <div className={`page-in${shown ? "" : " waiting"}`}>{children}</div>;
}
